import { useState, useCallback, useMemo } from 'react';
import Swal from 'sweetalert2';
import type { WebFrequency, FrequencyRequestStatus } from '@/mocks/superAdminFrequencies';
import { saFrequencyRequests as initialRequests, frequencyPreferredConfig as initialConfig } from '@/mocks/superAdminFrequencies';
import type { PreferredFrequencyConfig } from '@/mocks/superAdminFrequencies';
import NuevaSolicitudModal from './NuevaSolicitudModal';

interface ManagedRequest {
  id: number;
  radioName: string;
  radioId: number;
  owner: string;
  requestedFrequency: string;
  numericValue: number;
  currentFrequency: string;
  plan: string;
  country: string;
  responsible: string;
  status: FrequencyRequestStatus;
  paymentStatus: 'unpaid' | 'paid' | 'refunded';
  requestedAt: string;
  expiresAt: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  rejectedReason: string | null;
  invoiceGeneratedAt: string | null;
  invoiceId: string | null;
  paidAt: string | null;
  assignedAt: string | null;
  price: number;
  currency: string;
  tax: number;
  additionalCharge: number;
  totalPrice: number;
  notes: string | null;
  history: { timestamp: string; action: string; detail: string; performedBy: string }[];
}

interface FrecuenciaPreferidaPanelProps {
  frequencies: WebFrequency[];
  onFrequencyAssigned: (numericValue: number, radioName: string) => void;
}

const statusMeta: Record<FrequencyRequestStatus, { label: string; color: string; icon: string; bg: string }> = {
  requested: { label: 'Solicitada', color: 'text-secondary-700', icon: 'ri-mail-send-line', bg: 'bg-secondary-100' },
  pending_payment: { label: 'Pendiente Pago', color: 'text-sky-700', icon: 'ri-file-list-3-line', bg: 'bg-sky-100' },
  paid: { label: 'Pagada', color: 'text-emerald-700', icon: 'ri-bank-card-line', bg: 'bg-emerald-100' },
  under_review: { label: 'En Revisión', color: 'text-amber-700', icon: 'ri-search-eye-line', bg: 'bg-amber-100' },
  approved: { label: 'Aprobada', color: 'text-accent-700', icon: 'ri-shield-check-line', bg: 'bg-accent-100' },
  rejected: { label: 'Rechazada', color: 'text-red-600', icon: 'ri-close-circle-line', bg: 'bg-red-50' },
  assigned: { label: 'Asignada', color: 'text-primary-700', icon: 'ri-check-double-line', bg: 'bg-primary-100' },
  expired: { label: 'Expirada', color: 'text-stone-500', icon: 'ri-hourglass-line', bg: 'bg-stone-100' },
  cancelled: { label: 'Cancelada', color: 'text-foreground-400', icon: 'ri-forbid-line', bg: 'bg-foreground-200' },
};

const pipelineStages: { key: FrequencyRequestStatus; label: string; icon: string }[] = [
  { key: 'requested', label: 'Solicitada', icon: 'ri-mail-send-line' },
  { key: 'pending_payment', label: 'Pend. Pago', icon: 'ri-file-list-3-line' },
  { key: 'paid', label: 'Pagada', icon: 'ri-bank-card-line' },
  { key: 'under_review', label: 'Revisión', icon: 'ri-search-eye-line' },
  { key: 'approved', label: 'Aprobada', icon: 'ri-shield-check-line' },
  { key: 'assigned', label: 'Asignada', icon: 'ri-check-double-line' },
];

const terminalStatuses: FrequencyRequestStatus[] = ['rejected', 'assigned', 'expired', 'cancelled'];

const activeStatuses: FrequencyRequestStatus[] = ['requested', 'pending_payment', 'paid', 'under_review', 'approved'];

let nextId = 200;

export default function FrecuenciaPreferidaPanel({ frequencies, onFrequencyAssigned }: FrecuenciaPreferidaPanelProps) {
  const [requests, setRequests] = useState<ManagedRequest[]>(() =>
    initialRequests.map((r) => ({ ...r, history: r.history ? [...r.history] : [] })),
  );
  const [expanded, setExpanded] = useState(true);
  const [showNuevaSolicitud, setShowNuevaSolicitud] = useState(false);
  const [feedback, setFeedback] = useState<{ id: number; text: string; type: 'success' | 'error' } | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showPriceConfig, setShowPriceConfig] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<FrequencyRequestStatus | 'all'>('all');
  const [prefConfig, setPrefConfig] = useState<PreferredFrequencyConfig>(() => ({ ...initialConfig }));
  const [editingPricePlan, setEditingPricePlan] = useState<string | null>(null);
  const [editingPriceCountry, setEditingPriceCountry] = useState<string | null>(null);
  const [showRejectModal, setShowRejectModal] = useState<ManagedRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showNoteModal, setShowNoteModal] = useState<ManagedRequest | null>(null);
  const [newNote, setNewNote] = useState('');

  const showInline = useCallback((id: number, text: string, type: 'success' | 'error' = 'success') => {
    setFeedback({ id, text, type });
    setTimeout(() => setFeedback(null), 5000);
  }, []);

  const pendingRequests = useMemo(
    () => requests.filter((r) => activeStatuses.includes(r.status)),
    [requests],
  );

  const completedRequests = useMemo(
    () => requests.filter((r) => terminalStatuses.includes(r.status)),
    [requests],
  );

  const stageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    pipelineStages.forEach((s) => { counts[s.key] = 0; });
    requests.forEach((r) => {
      if (counts[r.status] !== undefined) counts[r.status]++;
    });
    return counts;
  }, [requests]);

  const filteredRequests = useMemo(() => {
    let result = requests;
    if (filterStatus !== 'all') result = result.filter((r) => r.status === filterStatus);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(
        (r) =>
          r.radioName.toLowerCase().includes(q) ||
          r.requestedFrequency.toLowerCase().includes(q) ||
          r.owner.toLowerCase().includes(q) ||
          r.country.toLowerCase().includes(q) ||
          (r.invoiceId && r.invoiceId.toLowerCase().includes(q)),
      );
    }
    return result;
  }, [requests, filterStatus, searchTerm]);

  const activeFiltered = filteredRequests.filter((r) => activeStatuses.includes(r.status));
  const completedFiltered = filteredRequests.filter((r) => terminalStatuses.includes(r.status));

  /* ─── CALCULAR PRECIO ─── */
  const calculatePrice = useCallback(
    (plan: string, country: string) => {
      const planPrice = prefConfig.plansPricing.find((p) => p.plan === plan);
      const countryPrice = prefConfig.countriesPricing.find((c) => c.country === country);
      const base = planPrice ? prefConfig.basePrice + planPrice.price : prefConfig.basePrice;
      const countryExtra = countryPrice ? countryPrice.additionalCharge : 0;
      const planExtra = planPrice ? planPrice.additionalCharge : 0;
      const subtotal = base + countryExtra + planExtra;

      const now = new Date().toISOString().split('T')[0];
      const isPromo =
        prefConfig.promotionalPrice !== null &&
        prefConfig.promotionalStartDate !== null &&
        prefConfig.promotionalEndDate !== null &&
        now >= prefConfig.promotionalStartDate &&
        now <= prefConfig.promotionalEndDate;

      const priceBeforeTax = isPromo ? prefConfig.promotionalPrice : subtotal;
      const tax = Math.round(priceBeforeTax * (prefConfig.taxRate / 100) * 100) / 100;
      const total = Math.round((priceBeforeTax + tax) * 100) / 100;

      return { price: subtotal, tax, totalPrice: total, isPromo };
    },
    [prefConfig],
  );

  /* ─── CREAR SOLICITUD ─── */
  const handleCreateRequest = useCallback(
    (data: { radioName: string; owner: string; requestedFrequency: string; numericValue: number; currentFrequency: string; plan: string; country: string; notes: string }) => {
      const now = new Date().toISOString();
      const pricing = calculatePrice(data.plan, data.country);
      const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

      const newReq: ManagedRequest = {
        id: nextId++,
        radioName: data.radioName,
        radioId: Math.floor(Math.random() * 100) + 1,
        owner: data.owner,
        requestedFrequency: data.requestedFrequency,
        numericValue: data.numericValue,
        currentFrequency: data.currentFrequency || 'No especificada',
        plan: data.plan,
        country: data.country,
        responsible: 'carlos.lopez@rfw.com',
        status: 'pending_payment',
        paymentStatus: 'unpaid',
        requestedAt: now,
        expiresAt,
        approvedAt: null,
        rejectedAt: null,
        rejectedReason: null,
        invoiceGeneratedAt: null,
        invoiceId: null,
        paidAt: null,
        assignedAt: null,
        price: pricing.price,
        currency: prefConfig.currency,
        tax: pricing.tax,
        additionalCharge: 0,
        totalPrice: pricing.totalPrice,
        notes: data.notes || null,
        history: [
          { timestamp: now, action: 'requested', detail: `Solicitud creada. Precio calculado: $${pricing.totalPrice} ${prefConfig.currency}${pricing.isPromo ? ' (precio promocional)' : ''}.`, performedBy: 'sistema@rfw.com' },
        ],
      };

      setRequests((prev) => [newReq, ...prev]);
      showInline(newReq.id, `Solicitud creada. Precio: $${pricing.totalPrice} ${prefConfig.currency}. Pendiente de pago.`, 'success');
    },
    [calculatePrice, prefConfig, showInline],
  );

  /* ─── GENERAR INVOICE ─── */
  const handleGenerateInvoice = useCallback(
    async (req: ManagedRequest) => {
      const result = await Swal.fire({
        title: '¿Generar invoice?',
        html: `<div style="text-align:left;font-size:12px;"><p><strong>Radio:</strong> ${req.radioName}</p><p><strong>Frecuencia:</strong> <span style="font-family:monospace;">${req.requestedFrequency}</span></p><p><strong>Total:</strong> $${req.totalPrice.toFixed(2)} ${req.currency}</p></div>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Generar invoice',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#0284c7',
        cancelButtonColor: '#d1d5db',
        customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700' },
      });
      if (result.isConfirmed) {
        const now = new Date().toISOString();
        const invoiceId = `INV-PF-${new Date().getFullYear()}-${String(nextId++).padStart(4, '0')}`;
        setRequests((prev) =>
          prev.map((r) =>
            r.id === req.id
              ? {
                  ...r,
                  status: 'pending_payment' as FrequencyRequestStatus,
                  invoiceGeneratedAt: now,
                  invoiceId,
                  history: [...r.history, { timestamp: now, action: 'invoice_generated', detail: `Invoice ${invoiceId} generada por $${req.totalPrice.toFixed(2)} ${req.currency}.`, performedBy: 'carlos.lopez@rfw.com' }],
                }
              : r,
          ),
        );
        showInline(req.id, `Invoice ${invoiceId} generada.`, 'success');
      }
    },
    [showInline],
  );

  /* ─── SIMULAR PAGO ─── */
  const handleSimularPago = useCallback(
    async (req: ManagedRequest) => {
      const result = await Swal.fire({
        title: '¿Simular pago?',
        html: `<div style="text-align:left;font-size:12px;"><p><strong>Radio:</strong> ${req.radioName}</p><p><strong>Invoice:</strong> ${req.invoiceId || 'Pendiente'}</p><p><strong>Total:</strong> $${req.totalPrice.toFixed(2)} ${req.currency}</p><p style="margin-top:8px;color:#6b7280;">Esto simula que la radio completó el pago.</p></div>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Confirmar pago',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#059669',
        cancelButtonColor: '#d1d5db',
        customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700' },
      });
      if (result.isConfirmed) {
        const now = new Date().toISOString();
        setRequests((prev) =>
          prev.map((r) =>
            r.id === req.id
              ? {
                  ...r,
                  status: 'paid' as FrequencyRequestStatus,
                  paymentStatus: 'paid' as const,
                  paidAt: now,
                  history: [...r.history, { timestamp: now, action: 'paid', detail: `Pago simulado por $${req.totalPrice.toFixed(2)} ${req.currency}.`, performedBy: 'sistema@rfw.com' }],
                }
              : r,
          ),
        );
        showInline(req.id, `Pago confirmado. Ahora en revisión.`, 'success');
      }
    },
    [showInline],
  );

  /* ─── ENVIAR A REVISIÓN ─── */
  const handleSendToReview = useCallback(
    (req: ManagedRequest) => {
      Swal.fire({
        title: '¿Enviar a revisión?',
        text: `${req.radioName} pasará a revisión por el Super Admin.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Enviar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d97706',
        cancelButtonColor: '#d1d5db',
        customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700' },
      }).then((r) => {
        if (r.isConfirmed) {
          const now = new Date().toISOString();
          setRequests((prev) =>
            prev.map((rr) =>
              rr.id === req.id
                ? { ...rr, status: 'under_review' as FrequencyRequestStatus, history: [...rr.history, { timestamp: now, action: 'under_review', detail: 'Enviada a revisión por Super Admin.', performedBy: 'carlos.lopez@rfw.com' }] }
                : rr,
            ),
          );
          showInline(req.id, 'En revisión.', 'success');
        }
      });
    },
    [showInline],
  );

  /* ─── APROBAR ─── */
  const handleAprobar = useCallback(
    async (req: ManagedRequest) => {
      if (req.paymentStatus !== 'paid' && req.totalPrice > 0) {
        await Swal.fire({
          icon: 'error',
          title: 'Pago requerido',
          text: 'No se puede aprobar una solicitud con precio mayor a cero sin confirmar el pago.',
          confirmButtonColor: '#1a1a1a',
          customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl' },
        });
        return;
      }

      const freq = frequencies.find((f) => Math.abs(f.numericValue - req.numericValue) < 0.001);
      if (!freq || (freq.status !== 'libre' && freq.status !== 'reservada')) {
        await Swal.fire({
          icon: 'error',
          title: 'Frecuencia no disponible',
          text: freq ? `${req.requestedFrequency} está ${freq.status === 'asignada' ? `asignada a ${freq.assignedRadio}` : freq.status === 'bloqueada' ? 'bloqueada' : freq.status}.` : 'Frecuencia fuera del rango.',
          confirmButtonColor: '#1a1a1a',
          customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl' },
        });
        return;
      }

      const result = await Swal.fire({
        title: '¿Aprobar solicitud?',
        html: `<div style="text-align:left;font-size:12px;"><p><strong>Radio:</strong> ${req.radioName}</p><p><strong>Frecuencia:</strong> <span style="font-family:monospace;">${req.requestedFrequency}</span></p><p style="margin-top:8px;color:#059669;">Al aprobar, la frecuencia se asignará automáticamente.</p></div>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Aprobar y asignar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#059669',
        cancelButtonColor: '#d1d5db',
        customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700' },
      });

      if (result.isConfirmed) {
        const now = new Date().toISOString();
        setRequests((prev) =>
          prev.map((r) =>
            r.id === req.id
              ? {
                  ...r,
                  status: 'assigned' as FrequencyRequestStatus,
                  approvedAt: now,
                  assignedAt: now,
                  history: [...r.history, { timestamp: now, action: 'approved', detail: 'Aprobada por Super Admin.', performedBy: 'carlos.lopez@rfw.com' }, { timestamp: now, action: 'assigned', detail: `Frecuencia ${req.requestedFrequency} asignada a ${req.radioName}.`, performedBy: 'sistema@rfw.com' }],
                }
              : r,
          ),
        );
        onFrequencyAssigned(req.numericValue, req.radioName);
        showInline(req.id, `Aprobada y asignada. ${req.requestedFrequency} → ${req.radioName}`, 'success');
      }
    },
    [frequencies, onFrequencyAssigned, showInline],
  );

  /* ─── RECHAZAR ─── */
  const handleOpenReject = useCallback((req: ManagedRequest) => {
    setRejectReason('');
    setShowRejectModal(req);
  }, []);

  const handleConfirmReject = useCallback(() => {
    if (!showRejectModal || !rejectReason.trim()) {
      Swal.fire({ icon: 'warning', title: 'Motivo requerido', text: 'Debés ingresar un motivo para rechazar la solicitud.', confirmButtonColor: '#1a1a1a', customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl' } });
      return;
    }
    const now = new Date().toISOString();
    setRequests((prev) =>
      prev.map((r) =>
        r.id === showRejectModal.id
          ? { ...r, status: 'rejected' as FrequencyRequestStatus, rejectedAt: now, rejectedReason: rejectReason.trim(), history: [...r.history, { timestamp: now, action: 'rejected', detail: `Rechazada: ${rejectReason.trim()}`, performedBy: 'carlos.lopez@rfw.com' }] }
          : r,
      ),
    );
    showInline(showRejectModal.id, `Solicitud rechazada.`, 'error');
    setShowRejectModal(null);
    setRejectReason('');
  }, [showRejectModal, rejectReason, showInline]);

  /* ─── CANCELAR ─── */
  const handleCancelar = useCallback(
    async (req: ManagedRequest) => {
      const result = await Swal.fire({
        title: '¿Cancelar solicitud?',
        html: `<div style="text-align:left;font-size:12px;"><p><strong>Radio:</strong> ${req.radioName}</p><p><strong>Frecuencia:</strong> <span style="font-family:monospace;">${req.requestedFrequency}</span></p><p style="color:#dc2626;margin-top:8px;">La frecuencia solicitada se liberará.</p></div>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, cancelar',
        cancelButtonText: 'No',
        confirmButtonColor: '#dc2626',
        cancelButtonColor: '#d1d5db',
        customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700' },
      });
      if (result.isConfirmed) {
        const now = new Date().toISOString();
        setRequests((prev) =>
          prev.map((r) =>
            r.id === req.id
              ? { ...r, status: 'cancelled' as FrequencyRequestStatus, history: [...r.history, { timestamp: now, action: 'cancelled', detail: 'Solicitud cancelada.', performedBy: 'carlos.lopez@rfw.com' }] }
              : r,
          ),
        );
        showInline(req.id, 'Solicitud cancelada.', 'error');
      }
    },
    [showInline],
  );

  /* ─── EXPIRAR ─── */
  const handleExpirar = useCallback(
    (req: ManagedRequest) => {
      Swal.fire({
        title: '¿Marcar como expirada?',
        text: 'Solo se puede expirar solicitudes que ya pasaron su fecha de vencimiento.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Expirar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#78716c',
        cancelButtonColor: '#d1d5db',
        customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700' },
      }).then((r) => {
        if (r.isConfirmed) {
          const now = new Date().toISOString();
          setRequests((prev) =>
            prev.map((rr) =>
              rr.id === req.id
                ? { ...rr, status: 'expired' as FrequencyRequestStatus, history: [...rr.history, { timestamp: now, action: 'expired', detail: 'Solicitud expirada.', performedBy: 'sistema@rfw.com' }] }
                : rr,
            ),
          );
          showInline(req.id, 'Solicitud expirada.', 'error');
        }
      });
    },
    [showInline],
  );

  /* ─── AGREGAR NOTA ─── */
  const handleOpenNote = useCallback((req: ManagedRequest) => {
    setNewNote('');
    setShowNoteModal(req);
  }, []);

  const handleSaveNote = useCallback(() => {
    if (!showNoteModal || !newNote.trim()) return;
    const now = new Date().toISOString();
    setRequests((prev) =>
      prev.map((r) =>
        r.id === showNoteModal.id
          ? { ...r, notes: r.notes ? `${r.notes}\n${newNote.trim()}` : newNote.trim(), history: [...r.history, { timestamp: now, action: 'note_added', detail: `Nota agregada: ${newNote.trim()}`, performedBy: 'carlos.lopez@rfw.com' }] }
          : r,
      ),
    );
    showInline(showNoteModal.id, 'Nota agregada.', 'success');
    setShowNoteModal(null);
    setNewNote('');
  }, [showNoteModal, newNote, showInline]);

  /* ─── LIBERAR FRECUENCIA ─── */
  const handleLiberar = useCallback(
    async (req: ManagedRequest) => {
      if (req.status !== 'assigned') return;
      const result = await Swal.fire({
        title: '¿Liberar frecuencia?',
        html: `<div style="text-align:left;font-size:12px;"><p><strong>Radio:</strong> ${req.radioName}</p><p><strong>Frecuencia:</strong> <span style="font-family:monospace;">${req.requestedFrequency}</span></p><p style="color:#ef4444;margin-top:8px;">${req.radioName} perderá esta frecuencia.</p></div>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, liberar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#d1d5db',
        customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700' },
      });
      if (result.isConfirmed) {
        const now = new Date().toISOString();
        setRequests((prev) =>
          prev.map((r) =>
            r.id === req.id
              ? { ...r, status: 'cancelled' as FrequencyRequestStatus, history: [...r.history, { timestamp: now, action: 'released', detail: `Frecuencia ${req.requestedFrequency} liberada.`, performedBy: 'carlos.lopez@rfw.com' }] }
              : r,
          ),
        );
        showInline(req.id, 'Frecuencia liberada.', 'error');
      }
    },
    [showInline],
  );

  /* ─── PRICE CONFIG ─── */
  const handleTogglePrefEnabled = () => {
    const next = !prefConfig.enabled;
    Swal.fire({
      title: next ? '¿Activar Freq. Preferida?' : '¿Desactivar?',
      text: next ? 'Las radios podrán solicitar frecuencias preferidas.' : 'Se desactivará el sistema.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: next ? 'Activar' : 'Desactivar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: next ? '#059669' : '#dc2626',
      cancelButtonColor: '#d1d5db',
      customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700' },
    }).then((r) => {
      if (r.isConfirmed) {
        setPrefConfig((prev) => ({ ...prev, enabled: next }));
        Swal.fire({ icon: 'success', title: next ? 'Activada' : 'Desactivada', toast: true, position: 'top-end', showConfirmButton: false, timer: 2000, timerProgressBar: true, customClass: { popup: 'rounded-2xl text-sm', title: 'text-xs font-semibold' } });
      }
    });
  };

  /* ─── EXPORT ─── */
  const handleExport = (format: 'csv' | 'excel' | 'pdf') => {
    Swal.fire({
      icon: 'success',
      title: `Exportado como ${format.toUpperCase()}`,
      text: `${filteredRequests.length} solicitudes exportadas en formato ${format.toUpperCase()} (simulado).`,
      confirmButtonColor: '#1a1a1a',
      customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl' },
    });
    setShowExport(false);
  };

  return (
    <>
      <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
        {/* Header */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between px-4 md:px-5 py-3.5 cursor-pointer hover:bg-background-50/50 transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
              <i className="ri-vip-diamond-line text-accent-600 text-sm"></i>
            </div>
            <div className="text-left">
              <h3 className="text-foreground-800 text-xs font-bold">Preferred Frequencies</h3>
              <p className="text-foreground-400 text-[10px]">
                {pendingRequests.length} activas · {completedRequests.filter((r) => r.status === 'assigned').length} asignadas · {requests.length} total
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${prefConfig.enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-foreground-200 text-foreground-500'}`}>
              {prefConfig.enabled ? 'ON' : 'OFF'}
            </span>
            {pendingRequests.length > 0 && (
              <span className="w-5 h-5 rounded-full bg-accent-500 text-background-50 text-[9px] font-bold flex items-center justify-center">
                {pendingRequests.length}
              </span>
            )}
            <i className={`${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400 text-sm`}></i>
          </div>
        </button>

        {expanded && (
          <div className="border-t border-background-200">
            {/* Pipeline stages */}
            <div className="px-4 md:px-5 py-3">
              <div className="flex items-center gap-1 overflow-x-auto pb-1">
                {pipelineStages.map((stage, idx) => (
                  <div key={stage.key} className="flex items-center gap-1 shrink-0">
                    <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[10px] font-medium whitespace-nowrap ${statusMeta[stage.key].bg} ${statusMeta[stage.key].color}`}>
                      <i className={`${stage.icon} text-[9px]`}></i>
                      <span>{stage.label}</span>
                      <span className="ml-0.5 opacity-60">{stageCounts[stage.key] || 0}</span>
                    </div>
                    {idx < pipelineStages.length - 1 && (
                      <i className="ri-arrow-right-line text-foreground-300 text-[9px] shrink-0"></i>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Controls: search, filter, actions */}
            <div className="px-4 md:px-5 pb-2 flex flex-col sm:flex-row items-start sm:items-center gap-2">
              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                <div className="relative w-full sm:w-48">
                  <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-400 text-[10px]"></i>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar solicitud..."
                    className="w-full pl-7 pr-7 py-1.5 bg-background-100 border border-background-200 rounded-lg text-[10px] outline-none focus:border-foreground-300"
                  />
                  {searchTerm && (
                    <button onClick={() => setSearchTerm('')} className="absolute right-1.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded bg-background-200 flex items-center justify-center cursor-pointer">
                      <i className="ri-close-line text-foreground-400 text-[8px]"></i>
                    </button>
                  )}
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as FrequencyRequestStatus | 'all')}
                  className="px-2.5 py-1.5 bg-background-100 border border-background-200 rounded-lg text-[10px] outline-none cursor-pointer"
                >
                  <option value="all">Todos los estados</option>
                  {Object.entries(statusMeta).map(([key, meta]) => (
                    <option key={key} value={key}>{meta.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                <button onClick={() => setShowPriceConfig(!showPriceConfig)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-50 text-amber-700 text-[10px] font-medium hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-settings-3-line text-[10px]"></i>
                  Precios
                </button>
                <button onClick={() => setShowExport(true)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-background-100 text-foreground-500 text-[10px] font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-download-line text-[10px]"></i>
                  Exportar
                </button>
                <button onClick={() => setShowNuevaSolicitud(true)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-foreground-950 text-background-50 text-[10px] font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-add-line text-[10px]"></i>
                  Nueva Solicitud
                </button>
              </div>
            </div>

            {/* Price Config Panel */}
            {showPriceConfig && (
              <div className="mx-4 md:mx-5 mb-3 p-4 rounded-xl bg-amber-50/60 border border-amber-200 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-amber-800 text-[10px] font-bold uppercase tracking-wider">Configuración de Precios</p>
                  <button onClick={() => setShowPriceConfig(false)} className="w-5 h-5 rounded-md bg-amber-100 flex items-center justify-center cursor-pointer hover:bg-amber-200 transition-colors">
                    <i className="ri-close-line text-amber-600 text-[10px]"></i>
                  </button>
                </div>

                {/* Main toggle */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-white border border-amber-100">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                      <i className="ri-toggle-line text-amber-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-foreground-800 text-[11px] font-semibold">Sistema Activo</p>
                      <p className="text-foreground-400 text-[9px]">{prefConfig.enabled ? 'Las radios pueden solicitar frecuencias preferidas.' : 'Sistema desactivado.'}</p>
                    </div>
                  </div>
                  <button onClick={handleTogglePrefEnabled} className={`w-9 h-5 rounded-full transition-colors relative cursor-pointer ${prefConfig.enabled ? 'bg-emerald-400' : 'bg-background-300'}`}>
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${prefConfig.enabled ? 'left-4' : 'left-0.5'}`}></div>
                  </button>
                </div>

                {/* Base price */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="p-3 rounded-lg bg-white border border-amber-100">
                    <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-1">Precio Base</p>
                    <div className="flex items-center gap-2">
                      <p className="text-foreground-800 text-sm font-bold">${prefConfig.basePrice.toFixed(2)}</p>
                      <span className="text-foreground-400 text-[9px]">{prefConfig.currency}</span>
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-white border border-amber-100">
                    <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-1">Impuesto</p>
                    <p className="text-foreground-800 text-sm font-bold">{prefConfig.taxRate}%</p>
                  </div>
                  <div className="p-3 rounded-lg bg-white border border-amber-100">
                    <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-1">Precio Promocional</p>
                    {prefConfig.promotionalPrice !== null ? (
                      <div>
                        <p className="text-accent-600 text-sm font-bold">${prefConfig.promotionalPrice.toFixed(2)}</p>
                        <p className="text-foreground-400 text-[8px]">{prefConfig.promotionalStartDate} → {prefConfig.promotionalEndDate}</p>
                      </div>
                    ) : (
                      <p className="text-foreground-300 text-xs">Sin promoción</p>
                    )}
                  </div>
                </div>

                {/* Plans pricing */}
                <div>
                  <p className="text-foreground-500 text-[9px] font-semibold uppercase tracking-wider mb-1.5">Precio por Plan</p>
                  <div className="flex items-center gap-1 flex-wrap">
                    {prefConfig.plansPricing.map((pp) => (
                      <div key={pp.plan} className="relative">
                        <button onClick={() => setEditingPricePlan(editingPricePlan === pp.plan ? null : pp.plan)} className={`px-2.5 py-1 rounded-full text-[9px] font-medium transition-all cursor-pointer whitespace-nowrap ${editingPricePlan === pp.plan ? 'bg-amber-200 text-amber-800' : 'bg-white border border-amber-100 text-foreground-600 hover:bg-amber-50'}`}>
                          {pp.plan}: ${pp.price}
                        </button>
                        {editingPricePlan === pp.plan && (
                          <div className="absolute top-full mt-1 left-0 z-10 bg-white border border-amber-200 rounded-xl p-3 space-y-2 shadow-lg min-w-[160px]">
                            <label className="block">
                              <span className="text-foreground-400 text-[8px] uppercase">Precio extra</span>
                              <input type="number" step="0.01" min="0" value={pp.price} onChange={(e) => setPrefConfig((prev) => ({ ...prev, plansPricing: prev.plansPricing.map((p) => (p.plan === pp.plan ? { ...p, price: parseFloat(e.target.value) || 0 } : p)) }))} className="w-full mt-0.5 px-2 py-1 bg-white border border-background-200 rounded-lg text-[10px] outline-none" />
                            </label>
                            <label className="block">
                              <span className="text-foreground-400 text-[8px] uppercase">Cargo adicional</span>
                              <input type="number" step="0.01" min="0" value={pp.additionalCharge} onChange={(e) => setPrefConfig((prev) => ({ ...prev, plansPricing: prev.plansPricing.map((p) => (p.plan === pp.plan ? { ...p, additionalCharge: parseFloat(e.target.value) || 0 } : p)) }))} className="w-full mt-0.5 px-2 py-1 bg-white border border-background-200 rounded-lg text-[10px] outline-none" />
                            </label>
                            <button onClick={() => setEditingPricePlan(null)} className="w-full px-2 py-1 rounded-lg bg-amber-100 text-amber-700 text-[9px] font-medium hover:bg-amber-200 cursor-pointer">Listo</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Countries pricing */}
                <div>
                  <p className="text-foreground-500 text-[9px] font-semibold uppercase tracking-wider mb-1.5">Cargo por País</p>
                  <div className="flex items-center gap-1 flex-wrap">
                    {prefConfig.countriesPricing.map((cp) => (
                      <div key={cp.country} className="relative">
                        <button onClick={() => setEditingPriceCountry(editingPriceCountry === cp.country ? null : cp.country)} className={`px-2.5 py-1 rounded-full text-[9px] font-medium transition-all cursor-pointer whitespace-nowrap ${editingPriceCountry === cp.country ? 'bg-amber-200 text-amber-800' : 'bg-white border border-amber-100 text-foreground-600 hover:bg-amber-50'}`}>
                          {cp.country}: +${cp.additionalCharge}
                        </button>
                        {editingPriceCountry === cp.country && (
                          <div className="absolute top-full mt-1 left-0 z-10 bg-white border border-amber-200 rounded-xl p-3 space-y-2 shadow-lg min-w-[160px]">
                            <label className="block">
                              <span className="text-foreground-400 text-[8px] uppercase">Cargo adicional</span>
                              <input type="number" step="0.01" min="0" value={cp.additionalCharge} onChange={(e) => setPrefConfig((prev) => ({ ...prev, countriesPricing: prev.countriesPricing.map((c) => (c.country === cp.country ? { ...c, additionalCharge: parseFloat(e.target.value) || 0 } : c)) }))} className="w-full mt-0.5 px-2 py-1 bg-white border border-background-200 rounded-lg text-[10px] outline-none" />
                            </label>
                            <button onClick={() => setEditingPriceCountry(null)} className="w-full px-2 py-1 rounded-lg bg-amber-100 text-amber-700 text-[9px] font-medium hover:bg-amber-200 cursor-pointer">Listo</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-red-50/70 rounded-lg p-3 text-[9px] text-red-600 flex items-start gap-2">
                  <i className="ri-error-warning-line text-[11px] mt-0.5 shrink-0"></i>
                  <span>Los cambios de precio no afectan solicitudes ya existentes. Aplican a nuevas solicitudes.</span>
                </div>
              </div>
            )}

            {/* Validation warning */}
            <div className="px-4 md:px-5 pb-2">
              <p className="text-foreground-300 text-[9px] leading-relaxed">
                Flujo: Radio solicita → Se calcula precio → Pendiente de pago → Pago simulado → Revisión SA → Aprueba/Rechaza → Asigna.
                <span className="text-foreground-400 font-medium"> No se asigna frecuencia ocupada. Pago requerido si precio &gt; $0. Dos radios no comparten frecuencia.</span>
              </p>
            </div>

            {/* Active requests */}
            {activeFiltered.length === 0 ? (
              <div className="px-4 md:px-5 pb-4">
                <div className="bg-background-100/70 rounded-xl p-6 text-center">
                  <i className="ri-inbox-line text-foreground-300 text-xl"></i>
                  <p className="text-foreground-400 text-xs mt-1.5">Sin solicitudes {filterStatus !== 'all' ? `en estado "${statusMeta[filterStatus].label}"` : 'activas'}</p>
                  <button onClick={() => { setFilterStatus('all'); setSearchTerm(''); setShowNuevaSolicitud(true); }} className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-foreground-950 text-background-50 text-[10px] font-bold hover:bg-foreground-800 transition-colors cursor-pointer">
                    <i className="ri-add-line text-[9px]"></i>Crear solicitud
                  </button>
                </div>
              </div>
            ) : (
              <div className="px-4 md:px-5 pb-4 space-y-1.5">
                {activeFiltered.map((req) => {
                  const isFeedbackTarget = feedback?.id === req.id;
                  const isExpired = req.expiresAt && new Date(req.expiresAt) < new Date();

                  return (
                    <div key={req.id}>
                      <div className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-xl border transition-colors ${isExpired && req.status !== 'expired' ? 'bg-red-50/40 border-red-200' : 'bg-background-100/70 border-background-200/70'}`}>
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="w-8 h-8 rounded-lg bg-background-200 flex items-center justify-center shrink-0">
                            <i className="ri-radio-line text-foreground-500 text-sm"></i>
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-foreground-800 text-xs font-semibold truncate">{req.radioName}</p>
                              <span className="text-foreground-300 text-[9px]">{req.country}</span>
                            </div>
                            <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                              <span className="font-mono text-foreground-600 text-[10px] font-semibold">{req.requestedFrequency}</span>
                              <span className="text-foreground-300 text-[9px]">·</span>
                              <span className="text-foreground-400 text-[10px]">{req.plan}</span>
                              <span className="text-foreground-300 text-[9px]">·</span>
                              <span className={`font-mono text-[10px] font-semibold ${req.paymentStatus === 'paid' ? 'text-emerald-600' : 'text-foreground-500'}`}>
                                ${req.totalPrice.toFixed(2)}
                              </span>
                              {req.invoiceId && (
                                <>
                                  <span className="text-foreground-300 text-[9px]">·</span>
                                  <span className="font-mono text-foreground-400 text-[9px]">{req.invoiceId}</span>
                                </>
                              )}
                              {isExpired && req.status !== 'expired' && (
                                <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 text-[8px] font-bold whitespace-nowrap">VENCIDA</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0 flex-wrap">
                          {/* Progress dots */}
                          <div className="hidden sm:flex items-center gap-1">
                            {pipelineStages.map((s, i) => {
                              const reqStageIdx = pipelineStages.findIndex((ps) => ps.key === req.status);
                              if (reqStageIdx === -1) return null;
                              const isActive = i <= reqStageIdx;
                              const isCurrent = i === reqStageIdx;
                              return (
                                <div key={s.key} className={`w-1.5 h-1.5 rounded-full ${isCurrent ? 'bg-accent-500 ring-2 ring-accent-200' : isActive ? 'bg-emerald-400' : 'bg-background-300'}`}></div>
                              );
                            })}
                          </div>

                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-medium whitespace-nowrap ${statusMeta[req.status].bg} ${statusMeta[req.status].color}`}>
                            <i className={`${statusMeta[req.status].icon} text-[8px]`}></i>
                            {statusMeta[req.status].label}
                          </span>

                          {/* Actions per status */}
                          <div className="flex items-center gap-1">
                            {/* Always available: note */}
                            <button onClick={() => handleOpenNote(req)} className="w-6 h-6 rounded-md bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors" title="Agregar nota">
                              <i className="ri-sticky-note-line text-foreground-500 text-[10px]"></i>
                            </button>

                            {/* Requested: generate invoice + cancel */}
                            {req.status === 'requested' && (
                              <>
                                <button onClick={() => handleGenerateInvoice(req)} className="px-2.5 py-1.5 rounded-lg bg-sky-500 text-white text-[9px] font-bold hover:bg-sky-600 transition-colors cursor-pointer whitespace-nowrap">
                                  <i className="ri-file-list-3-line mr-1 text-[8px]"></i>Invoice
                                </button>
                                <button onClick={() => handleCancelar(req)} className="w-6 h-6 rounded-md bg-red-50 flex items-center justify-center cursor-pointer hover:bg-red-100 transition-colors" title="Cancelar">
                                  <i className="ri-close-line text-red-400 text-[10px]"></i>
                                </button>
                              </>
                            )}

                            {/* Pending payment: simulate payment + cancel */}
                            {req.status === 'pending_payment' && (
                              <>
                                <button onClick={() => handleSimularPago(req)} className="px-2.5 py-1.5 rounded-lg bg-emerald-500 text-white text-[9px] font-bold hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap">
                                  <i className="ri-bank-card-line mr-1 text-[8px]"></i>Pagar
                                </button>
                                <button onClick={() => handleCancelar(req)} className="w-6 h-6 rounded-md bg-red-50 flex items-center justify-center cursor-pointer hover:bg-red-100 transition-colors" title="Cancelar">
                                  <i className="ri-close-line text-red-400 text-[10px]"></i>
                                </button>
                              </>
                            )}

                            {/* Paid: send to review + cancel */}
                            {req.status === 'paid' && (
                              <>
                                <button onClick={() => handleSendToReview(req)} className="px-2.5 py-1.5 rounded-lg bg-amber-500 text-white text-[9px] font-bold hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap">
                                  <i className="ri-search-eye-line mr-1 text-[8px]"></i>Revisar
                                </button>
                                <button onClick={() => handleCancelar(req)} className="w-6 h-6 rounded-md bg-red-50 flex items-center justify-center cursor-pointer hover:bg-red-100 transition-colors" title="Cancelar">
                                  <i className="ri-close-line text-red-400 text-[10px]"></i>
                                </button>
                              </>
                            )}

                            {/* Under review: approve + reject */}
                            {req.status === 'under_review' && (
                              <>
                                <button onClick={() => handleAprobar(req)} className="px-2.5 py-1.5 rounded-lg bg-emerald-500 text-white text-[9px] font-bold hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap">
                                  <i className="ri-check-line mr-1 text-[8px]"></i>Aprobar
                                </button>
                                <button onClick={() => handleOpenReject(req)} className="px-2.5 py-1.5 rounded-lg bg-red-500 text-white text-[9px] font-bold hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap">
                                  <i className="ri-close-line mr-1 text-[8px]"></i>Rechazar
                                </button>
                              </>
                            )}

                            {/* Approved: nothing (it's waiting to be assigned, already auto-assigned by approve) */}
                            {req.status === 'approved' && (
                              <button onClick={() => handleAprobar(req)} className="px-2.5 py-1.5 rounded-lg bg-primary-500 text-white text-[9px] font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                                <i className="ri-check-double-line mr-1 text-[8px]"></i>Asignar
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Inline feedback */}
                      {isFeedbackTarget && (
                        <div className={`mt-1 flex items-center gap-1.5 px-3 py-2 rounded-lg text-[10px] font-medium ${feedback.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                          <i className={`${feedback.type === 'success' ? 'ri-check-line text-emerald-500' : 'ri-error-warning-line text-red-400'} text-xs`}></i>
                          {feedback.text}
                        </div>
                      )}

                      {/* Expiry warning */}
                      {isExpired && req.status !== 'expired' && (
                        <div className="mt-1">
                          <button onClick={() => handleExpirar(req)} className="w-full flex items-center justify-between px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-600 text-[9px] font-medium cursor-pointer hover:bg-red-100 transition-colors">
                            <span>Esta solicitud está vencida ({new Date(req.expiresAt!).toLocaleDateString()})</span>
                            <span className="underline">Marcar como expirada →</span>
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Completed requests */}
            {completedFiltered.length > 0 && (
              <div className="border-t border-background-200">
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="w-full flex items-center justify-between px-4 md:px-5 py-2.5 cursor-pointer hover:bg-background-50/50 transition-colors"
                >
                  <span className="text-foreground-500 text-[10px] font-medium">
                    Historial ({completedFiltered.length} solicitud{completedFiltered.length !== 1 ? 'es' : ''})
                  </span>
                  <i className={`${showHistory ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400 text-sm`}></i>
                </button>
                {showHistory && (
                  <div className="px-4 md:px-5 pb-3 space-y-1">
                    {completedFiltered.map((req) => (
                      <div key={req.id} className="flex flex-col sm:flex-row sm:items-center gap-2 p-2.5 rounded-lg bg-background-100/50">
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${statusMeta[req.status].bg}`}>
                            <i className={`${statusMeta[req.status].icon} ${statusMeta[req.status].color} text-[10px]`}></i>
                          </div>
                          <div className="min-w-0">
                            <p className="text-foreground-600 text-[10px] font-medium truncate">{req.radioName}</p>
                            <div className="flex items-center gap-1.5">
                              <span className="font-mono text-foreground-400 text-[9px]">{req.requestedFrequency}</span>
                              {req.rejectedReason && (
                                <span className="text-red-400 text-[8px] truncate max-w-[120px]" title={req.rejectedReason}>{req.rejectedReason}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-medium whitespace-nowrap ${statusMeta[req.status].bg} ${statusMeta[req.status].color}`}>
                            {statusMeta[req.status].label}
                          </span>
                          {req.status === 'assigned' && (
                            <button onClick={() => handleLiberar(req)} className="w-5 h-5 rounded-md bg-red-50 flex items-center justify-center cursor-pointer hover:bg-red-100 transition-colors" title="Liberar frecuencia">
                              <i className="ri-link-unlink text-red-400 text-[8px]"></i>
                            </button>
                          )}
                          <span className="text-foreground-300 text-[8px]">{req.requestedAt.split('T')[0]}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Nueva Solicitud Modal */}
      {showNuevaSolicitud && (
        <NuevaSolicitudModal
          frequencies={frequencies}
          onClose={() => setShowNuevaSolicitud(false)}
          onCreate={handleCreateRequest}
        />
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setShowRejectModal(null); setRejectReason(''); }}></div>
          <div className="relative bg-background-50 rounded-2xl w-full max-w-md shadow-xl overflow-hidden">
            <div className="p-5 border-b border-background-200">
              <h3 className="text-foreground-950 font-bold text-sm">Rechazar Solicitud</h3>
              <p className="text-foreground-400 text-xs mt-0.5">{showRejectModal.radioName} · {showRejectModal.requestedFrequency}</p>
            </div>
            <div className="p-5 space-y-3">
              <label className="block">
                <span className="text-foreground-500 text-[9px] font-semibold uppercase tracking-wider">Motivo del rechazo <span className="text-red-500">*</span></span>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Explicá por qué se rechaza esta solicitud..."
                  maxLength={500}
                  rows={3}
                  className="w-full mt-1 px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-red-300 resize-y"
                ></textarea>
                <p className="text-foreground-300 text-[8px] mt-0.5">{rejectReason.length}/500</p>
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 p-5 border-t border-background-200">
              <button onClick={() => { setShowRejectModal(null); setRejectReason(''); }} className="px-4 py-2 rounded-xl bg-background-200 text-foreground-600 text-xs font-medium hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={handleConfirmReject} disabled={!rejectReason.trim()} className="px-4 py-2 rounded-xl bg-red-500 text-white text-xs font-bold hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-30 disabled:cursor-not-allowed">
                <i className="ri-close-line mr-1 text-[11px]"></i>Rechazar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowNoteModal(null)}></div>
          <div className="relative bg-background-50 rounded-2xl w-full max-w-md shadow-xl overflow-hidden">
            <div className="p-5 border-b border-background-200">
              <h3 className="text-foreground-950 font-bold text-sm">Agregar Nota</h3>
              <p className="text-foreground-400 text-xs mt-0.5">{showNoteModal.radioName} · {showNoteModal.requestedFrequency}</p>
            </div>
            <div className="p-5 space-y-3">
              {showNoteModal.notes && (
                <div className="bg-background-100 rounded-xl p-3 text-foreground-500 text-[10px] max-h-32 overflow-y-auto whitespace-pre-wrap">
                  {showNoteModal.notes}
                </div>
              )}
              <label className="block">
                <span className="text-foreground-500 text-[9px] font-semibold uppercase tracking-wider">Nueva nota</span>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Escribí una nota..."
                  maxLength={300}
                  rows={2}
                  className="w-full mt-1 px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 resize-y"
                ></textarea>
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 p-5 border-t border-background-200">
              <button onClick={() => setShowNoteModal(null)} className="px-4 py-2 rounded-xl bg-background-200 text-foreground-600 text-xs font-medium hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={handleSaveNote} disabled={!newNote.trim()} className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-30 disabled:cursor-not-allowed">
                <i className="ri-check-line mr-1 text-[11px]"></i>Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExport && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowExport(false)}></div>
          <div className="relative bg-background-50 rounded-2xl w-full max-w-xs shadow-xl overflow-hidden">
            <div className="p-5 border-b border-background-200 text-center">
              <h3 className="text-foreground-950 font-bold text-sm">Exportar Solicitudes</h3>
              <p className="text-foreground-400 text-xs mt-0.5">{filteredRequests.length} solicitudes</p>
            </div>
            <div className="p-5 space-y-2">
              <button onClick={() => handleExport('csv')} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-background-100 text-foreground-700 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer">
                <i className="ri-file-text-line text-emerald-500 text-base"></i>Exportar CSV
              </button>
              <button onClick={() => handleExport('excel')} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-background-100 text-foreground-700 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer">
                <i className="ri-file-excel-2-line text-emerald-600 text-base"></i>Exportar Excel
              </button>
              <button onClick={() => handleExport('pdf')} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-background-100 text-foreground-700 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer">
                <i className="ri-file-pdf-2-line text-red-500 text-base"></i>Exportar PDF
              </button>
            </div>
            <div className="p-5 border-t border-background-200 text-center">
              <button onClick={() => setShowExport(false)} className="text-foreground-400 text-[10px] font-medium hover:text-foreground-600 cursor-pointer">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}